/**
 * User Submit Ticket Page
 *
 * Simplified ticket creation form for end users.
 * Automatically creates ticket for the logged-in user.
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useTicketRepository } from '../../data/hooks';
import { useCurrentUser } from '../../hooks';
import { TicketTypeSelector } from '../../components/TicketTypeSelector';
import { CategorySelector } from '../../components/CategorySelector';
import { AppTextField } from '../../components/ui/AppTextField';
import { AppButton } from '../../components/ui/AppButton';
import { AppSnackbar } from '../../components/ui/AppSnackbar';
import {
  TicketType,
  Priority,
  type IncidentCategory,
  type IncidentSubcategory,
  type RequestCategory,
  type RequestSubcategory,
  Location,
} from '../../types/ticket';
import type { Department } from '../../types/knowledgeBase';
import type { CreateTicketDTO } from '../../types/dto';
import {
  type UserSubmitFormData,
  type UserSubmitFormErrors,
  DEFAULT_USER_SUBMIT_FORM,
  TICKET_VALIDATION,
  PRIORITY_DESCRIPTIONS,
  validateUserSubmitForm,
  hasFormErrors,
} from '../../types/ticketForms';
import './UserSubmitTicketPage.css';

/** Generate a user-submitted ticket number (USR prefix) */
function generateUserTicketNumber(): string {
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 5).toUpperCase();
  return `USR${timestamp}${random}`.substring(0, 10);
}

export function UserSubmitTicketPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const ticketRepo = useTicketRepository();
  const currentUser = useCurrentUser();

  // Form state
  const [formData, setFormData] = useState<UserSubmitFormData>(DEFAULT_USER_SUBMIT_FORM);
  const [errors, setErrors] = useState<UserSubmitFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Character counts
  const descriptionCount = formData.shortDescription.length;
  const notesCount = formData.notes.length;

  // Update a single field
  const updateField = useCallback(
    <K extends keyof UserSubmitFormData>(field: K, value: UserSubmitFormData[K]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Clear error for this field
      if (errors[field]) {
        setErrors((prev) => {
          const updated = { ...prev };
          delete updated[field];
          return updated;
        });
      }
    },
    [errors]
  );

  // Handle ticket type change - clears category and subcategory
  const handleTicketTypeChange = useCallback(
    (type: TicketType) => {
      setFormData((prev) => ({
        ...prev,
        ticketType: type,
        category: '',
        subcategory: '',
      }));
    },
    []
  );

  // Handle category change
  const handleCategoryChange = useCallback(
    (category: IncidentCategory | RequestCategory | '') => {
      updateField('category', category);
    },
    [updateField]
  );

  // Handle subcategory change
  const handleSubcategoryChange = useCallback(
    (subcategory: IncidentSubcategory | RequestSubcategory | '') => {
      updateField('subcategory', subcategory);
    },
    [updateField]
  );

  // Validate form
  const validateForm = useCallback((): boolean => {
    const validationErrors = validateUserSubmitForm(formData);
    setErrors(validationErrors);
    return !hasFormErrors(validationErrors);
  }, [formData]);

  // Submit form
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!validateForm()) {
        setSnackbar({
          open: true,
          message: 'Please fix the errors in the form',
          severity: 'error',
        });
        return;
      }

      setIsSubmitting(true);

      try {
        const ticketNumber = generateUserTicketNumber();
        const userLocation = (currentUser as unknown as { location?: Location }).location || Location.US_DEARBORN;

        // User portal only creates Incident or Request types
        const createDTO = {
          ticketNumber,
          shortDescription: formData.shortDescription.trim(),
          ticketType: formData.ticketType as TicketType,
          category: formData.category as IncidentCategory | RequestCategory,
          subcategory: formData.subcategory as IncidentSubcategory | RequestSubcategory,
          priority: formData.priority,
          location: userLocation,
          requesterId: currentUser.id,
          requesterName: currentUser.name,
          requesterEmail: currentUser.email,
          requesterDepartment: currentUser.department as Department,
          createdBy: currentUser.id,
          createdByName: currentUser.name,
          notes: formData.notes.trim() || undefined,
        } as CreateTicketDTO;

        const ticket = await ticketRepo.create(createDTO);

        setSubmitted(ticket.ticketNumber);
        setSnackbar({
          open: true,
          message: `Ticket ${ticket.ticketNumber} submitted successfully!`,
          severity: 'success',
        });
      } catch (error) {
        console.error('Failed to submit ticket:', error);
        setSnackbar({
          open: true,
          message: 'Failed to submit ticket. Please try again.',
          severity: 'error',
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, currentUser, ticketRepo, validateForm]
  );

  // Submit another ticket
  const handleSubmitAnother = useCallback(() => {
    setSubmitted(null);
    setFormData(DEFAULT_USER_SUBMIT_FORM);
    setErrors({});
  }, []);

  // View submitted ticket
  const handleViewTickets = useCallback(() => {
    navigate('/tickets');
  }, [navigate]);

  // Success view
  if (submitted) {
    return (
      <div className="user-submit-ticket" data-theme={theme}>
        <div className="user-submit-ticket__success">
          <div className="user-submit-ticket__success-icon">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
              <circle cx="32" cy="32" r="30" stroke="currentColor" strokeWidth="4" />
              <path
                d="M20 32l8 8 16-16"
                stroke="currentColor"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h1 className="user-submit-ticket__success-title">Ticket Submitted!</h1>
          <p className="user-submit-ticket__success-message">
            Your ticket <strong>{submitted}</strong> has been submitted successfully.
            Our IT team will review it shortly.
          </p>
          <div className="user-submit-ticket__success-actions">
            <AppButton variant="secondary" onClick={handleSubmitAnother}>
              Submit Another Ticket
            </AppButton>
            <AppButton variant="primary" onClick={handleViewTickets}>
              View All Tickets
            </AppButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-submit-ticket" data-theme={theme}>
      <div className="user-submit-ticket__container">
        {/* User Info Header */}
        <div className="user-submit-ticket__user-info">
          <div className="user-submit-ticket__avatar">
            {currentUser.name.charAt(0).toUpperCase()}
          </div>
          <div className="user-submit-ticket__user-details">
            <span className="user-submit-ticket__user-name">{currentUser.name}</span>
            <span className="user-submit-ticket__user-email">{currentUser.email}</span>
          </div>
        </div>

        <div className="user-submit-ticket__header">
          <h1 className="user-submit-ticket__title">Submit a Ticket</h1>
          <p className="user-submit-ticket__subtitle">
            Need help with something? Let us know and we'll get back to you as soon as possible.
          </p>
        </div>

        <form className="user-submit-ticket__form" onSubmit={handleSubmit}>
          {/* Ticket Type */}
          <div className="user-submit-ticket__section">
            <h2 className="user-submit-ticket__section-title">What do you need help with?</h2>
            <TicketTypeSelector
              value={formData.ticketType}
              onChange={handleTicketTypeChange}
              error={!!errors.ticketType}
              errorText={errors.ticketType}
              disabled={isSubmitting}
            />
          </div>

          {/* Category */}
          <div className="user-submit-ticket__section">
            <h2 className="user-submit-ticket__section-title">Category</h2>
            <CategorySelector
              ticketType={formData.ticketType}
              category={formData.category}
              subcategory={formData.subcategory}
              onCategoryChange={handleCategoryChange}
              onSubcategoryChange={handleSubcategoryChange}
              categoryError={errors.category}
              subcategoryError={errors.subcategory}
              disabled={isSubmitting}
            />
          </div>

          {/* Priority */}
          <div className="user-submit-ticket__section">
            <h2 className="user-submit-ticket__section-title">How urgent is this?</h2>
            <div className="user-submit-ticket__priority-options">
              {Object.values(Priority).map((p) => (
                <label
                  key={p}
                  className={`user-submit-ticket__priority-option ${
                    formData.priority === p ? 'user-submit-ticket__priority-option--selected' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="priority"
                    value={p}
                    checked={formData.priority === p}
                    onChange={() => updateField('priority', p)}
                    disabled={isSubmitting}
                  />
                  <span className={`user-submit-ticket__priority-badge user-submit-ticket__priority-badge--${p.toLowerCase()}`}>
                    {p}
                  </span>
                  <span className="user-submit-ticket__priority-text">
                    {PRIORITY_DESCRIPTIONS[p]}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Description */}
          <div className="user-submit-ticket__section">
            <h2 className="user-submit-ticket__section-title">Describe your issue</h2>
            <AppTextField
              label="What's happening?"
              value={formData.shortDescription}
              onChange={(e) => updateField('shortDescription', e.target.value)}
              placeholder="Briefly describe the problem or what you need"
              error={!!errors.shortDescription}
              helperText={
                errors.shortDescription ||
                `${descriptionCount}/${TICKET_VALIDATION.DESCRIPTION_MAX_LENGTH} characters`
              }
              required
              disabled={isSubmitting}
              multiline
              rows={2}
              inputProps={{ maxLength: TICKET_VALIDATION.DESCRIPTION_MAX_LENGTH }}
            />
          </div>

          {/* Notes */}
          <div className="user-submit-ticket__section">
            <h2 className="user-submit-ticket__section-title">Additional Details (Optional)</h2>
            <AppTextField
              label="Any other information that might help?"
              value={formData.notes}
              onChange={(e) => updateField('notes', e.target.value)}
              placeholder="Error messages, steps you've already tried, etc."
              helperText={
                errors.notes ||
                `${notesCount}/${TICKET_VALIDATION.USER_NOTES_MAX_LENGTH} characters`
              }
              error={!!errors.notes}
              disabled={isSubmitting}
              multiline
              rows={3}
              inputProps={{ maxLength: TICKET_VALIDATION.USER_NOTES_MAX_LENGTH }}
            />
          </div>

          {/* Submit */}
          <div className="user-submit-ticket__actions">
            <AppButton
              type="submit"
              variant="primary"
              size="large"
              disabled={isSubmitting}
              loading={isSubmitting}
              fullWidth
            >
              {isSubmitting ? 'Submitting...' : 'Submit Ticket'}
            </AppButton>
          </div>
        </form>
      </div>

      <AppSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </div>
  );
}
